import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import { FormTextField } from "@/components/UiKit"
import type { BulkPriceChangeUpdateInput } from "@/graphql/codegen/graphql"
import { PriceChangeType } from "@/graphql/codegen/graphql"
import type { PriceChangeSubmitContext } from "@/screens/Pricing/components/PriceChangeTables"
import { useSubmitPriceChanges } from "@/screens/Pricing/hooks/api/useSubmitPriceChanges"
import type { SubmitPriceChangeSchemaFormValues } from "@/screens/Pricing/schema/SumbitPriceChangeSchema"
import { SubmitPriceChangeSchema } from "@/screens/Pricing/schema/SumbitPriceChangeSchema"

interface SubmitPricingDialogProps {
  onClose: () => void
  onSuccess: (priceChangeSubmissionId: string) => void
  onBeforeSubmit: () => void
  submitContext: PriceChangeSubmitContext
  bulkUpdate?: BulkPriceChangeUpdateInput
}

const SubmitPricingDialog: FC<SubmitPricingDialogProps> = ({
  onClose,
  onSuccess,
  onBeforeSubmit,
  submitContext,
  bulkUpdate,
}) => {
  const { submitPriceChanges, isSubmitting } = useSubmitPriceChanges()
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const methods = useForm<SubmitPriceChangeSchemaFormValues>({
    defaultValues: {
      reasonForUpdate: "",
    },
    mode: "onChange",
    resolver: zodResolver(SubmitPriceChangeSchema),
  })

  const {
    formState: { isDirty, isValid },
  } = methods

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSubmit = async (data: SubmitPriceChangeSchemaFormValues) => {
    onBeforeSubmit()
    const isDraft = submitContext.filter.changeType === PriceChangeType.SellingPrice

    const priceChangeSubmissionId = await submitPriceChanges({
      filter: submitContext.filter,
      manualOverrides: submitContext.priceChangeItems,
      reasonForUpdate: data.reasonForUpdate as string,
      ...(bulkUpdate ? { bulkUpdate } : {}),
      isDraft,
    })

    if (!priceChangeSubmissionId) {
      onClose()
      return
    }
    onSuccess(priceChangeSubmissionId)
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth maxWidth={"md"}>
        <DialogTitle className='pl-10 font-normal text-primary'>Submit Price Changes</DialogTitle>
        <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
          <CloseOutlined />
        </IconButton>
        <Divider />
        <DialogContent classes={{ root: "p-10" }}>
          <FormProvider {...methods}>
            <form>
              <FormTextField
                name='reasonForUpdate'
                control={methods.control}
                label='Reason for Update'
                multiline
                required
                rows={4}
                placeholder='Enter reason for update'
                className='mt-3'
                data-testid='pricing.submit-pricing-form.reason-field'
              />
            </form>
          </FormProvider>
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button appearance='outlined' onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={!isValid || !isDirty || isSubmitting} onClick={methods.handleSubmit(handleSubmit)}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SubmitPricingDialog
